/*
 * Reading an athlete's own training back to them.
 *
 * # WHAT THIS SCREEN IS FOR
 *
 * One question: am I getting stronger at this. Everything here is subordinate to
 * answering it from the top of the page, because the alternative — a table of
 * every set ever logged — is a thing people scroll once and never open again.
 *
 * # WHY THE MOVEMENT AND NOT THE SESSION
 *
 * A history keyed on a session dies at every block boundary: prescriptions are
 * copied when a block is assigned, so the ids change and the old sets belong to
 * entries nothing reads any more. See routes/history.go, and §4 of
 * docs/mvp-funnel-2026-09-21.md, which recommended linking exactly that.
 */

/** A set with a real load, as opposed to timed or bodyweight work. */
function isLoaded(set) {
  return set.durationSeconds == null && set.distanceMetres == null && set.weight > 0;
}

/**
 * The heaviest set of a day, or null.
 *
 * # WHY THE TOP SET AND NOT THE AVERAGE
 *
 * Most sessions end lighter than they start — back-off work, drop sets — so a
 * mean falls when somebody adds a back-off set to a day they lifted MORE on.
 * That is the exact opposite of what the number is for. The top set is the one
 * an athlete would name if you asked them what they did.
 *
 * Null rather than 0 for a day of timed or bodyweight work: absent is not zero,
 * and "0 kg" beside a plank reads as a failed lift.
 */
export function topSet(day) {
  const loaded = (day?.sets ?? []).filter(isLoaded);
  if (loaded.length === 0) return null;
  return loaded.reduce((best, set) => (set.weight > best.weight ? set : best));
}

/**
 * How a day's top load compares with the one before it.
 *
 * Returns 'up', 'down', 'same', or null when there is nothing to compare —
 * which is every first day, and every day of work that carries no load.
 *
 * # WHY NOT A PERCENTAGE
 *
 * The comparison is the point and the arithmetic is not: "+2.3%" invites a
 * reader to treat session-to-session noise as a trend, on data where a different
 * rep count moves the number more than the athlete did. Direction is the honest
 * resolution of one day against one other.
 */
export function loadTrend(days, index) {
  const current = topSet(days?.[index]);
  const previous = topSet(days?.[index + 1]);
  if (!current || !previous) return null;
  if (current.weight > previous.weight) return 'up';
  if (current.weight < previous.weight) return 'down';
  return 'same';
}

/*
 * Which of two numbers a day is ranked on, and against which neighbour.
 *
 * # WHY THE ESTIMATE WINS WHEN THERE IS ONE
 *
 * Top load answers "what was the heaviest thing on the bar", which stops being
 * the same question as "am I getting stronger" the moment the rep count moves.
 * 95 kg x 8 is a better day than 100 kg x 5 on a lighter bar — ranked on load,
 * an athlete reads a week they got stronger as a week they went backwards.
 *
 * Worse, the estimate is already ON the row. An arrow keying on load beside a
 * higher estimated single is a row that contradicts itself, which is worse than
 * either number alone.
 *
 * # WHY THE FALLBACK IS PER COMPARISON
 *
 * The server omits an estimate above twelve reps, for bodyweight and timed work,
 * and outside the sane-max bounds. So a day can have one while the day before it
 * does not, and THAT PAIR has to fall back to top load — comparing an estimate
 * against a top load would rank two different quantities and call the difference
 * progress. Decided per pair, and the basis is carried out so the sentence can
 * say which claim it is making.
 */
function compare(current, previous) {
  if (current > previous) return 'up';
  if (current < previous) return 'down';
  return 'same';
}

export function dayTrend(days, index) {
  const current = days?.[index];
  const previous = days?.[index + 1];
  if (!current || !previous) return null;

  if (current.estimatedMax != null && previous.estimatedMax != null) {
    return {
      direction: compare(current.estimatedMax, previous.estimatedMax),
      basis: 'estimate',
      current: current.estimatedMax,
      previous: previous.estimatedMax,
    };
  }

  const trend = loadTrend(days, index);
  if (!trend) return null;
  return {
    direction: trend,
    basis: 'top',
    current: topSet(current).weight,
    previous: topSet(previous).weight,
  };
}

/** A day's sets as prose: "3 sets · 100 kg × 5". */
export function daySummary(day, unit) {
  const sets = day?.sets ?? [];
  if (sets.length === 0) return '';

  const count = `${sets.length} ${sets.length === 1 ? 'set' : 'sets'}`;
  const top = topSet(day);
  if (!top) return count;
  return `${count} · ${top.weight} ${unit} × ${top.repsPerformed}`;
}

/**
 * The sentence naming what changed since the athlete last did this.
 *
 * Null when there is no comparison to make. A first day says nothing rather than
 * "no change yet" — the screen is already showing them the session they logged,
 * and an absence framed as a result reads as a verdict on their training.
 */
export function progressNote(history) {
  const days = history?.days ?? [];
  const trend = dayTrend(days, 0);
  if (!trend) return null;

  const unit = history.unit ?? 'kg';
  // The sentence names WHICH claim it is making. The two can disagree — that is
  // the whole reason the estimate is preferred — so collapsing them into one
  // string would leave a reader unable to tell what moved.
  const what = trend.basis === 'estimate' ? 'estimated single' : 'top set';

  if (trend.direction === 'same') {
    return trend.basis === 'estimate'
      ? `Same estimated single as last time: ${trend.current} ${unit}.`
      : `Same top load as last time: ${trend.current} ${unit}.`;
  }

  const direction = trend.direction === 'up' ? 'Up' : 'Down';
  const delta = Math.abs(
    Math.round((trend.current - trend.previous) * 100) / 100
  );
  return `${direction} ${delta} ${unit} on your ${what} since last time.`;
}

/**
 * How long ago a date was, in words.
 *
 * Whole days, computed from calendar dates rather than elapsed hours: a session
 * logged yesterday evening and read this morning is "yesterday", not "14 hours".
 */
export function sinceDays(dateISO, today = new Date()) {
  if (!dateISO) return null;
  const then = Date.parse(`${dateISO}T00:00:00Z`);
  if (Number.isNaN(then)) return null;

  const now = Date.UTC(
    today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()
  );
  const days = Math.round((now - then) / 86400000);
  if (days < 0) return null;
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 14) return `${days} days ago`;
  if (days < 60) return `${Math.floor(days / 7)} weeks ago`;
  return `${Math.floor(days / 30)} months ago`;
}
